import { createAsyncThunk } from "@reduxjs/toolkit"

import { rejectWithHandledError } from "../../../utils/handlers/reject-with-handled-error.ts"
import type { RootState } from "../../../store.ts"
import type { AuditLogEntry } from "../../../types/audit-log.types.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import type { ProjectEditableRowData } from "../../Projects/ProjectsListPage/components/ProjectListEditableRow.tsx"
import { type SubcontractorFormData } from "../../SubcontractorsPage/components/SubcontractorForm/subcontractor-form-data.ts"
import { type SubcontractorListItem } from "../../SubcontractorsPage/types/types.ts"
import { rejectPendingCommitments } from "../../Projects/api/pending-commitments.ts"
import * as services from "../api/project.services"
import {
  instanceKeyOf,
  isRepeatedTypeRefusal
} from "../helpers/repeated-project-type.ts"
import { orderByIds } from "../../../utils/object/order-by-ids.ts"
import {
  type ProjectCreateData,
  type ProjectTypeContractDecisionData,
  type ProjectTypeData,
  type ProjectTypeOrderItem
} from "../api/services.types.ts"
import {
  type Project,
  type ProjectTypeDetail,
  type ProjectTypeFormData,
  type ProjectUpdateData
} from "../types/project.types.ts"
import { projectSliceName } from "./project.constants.ts"
import { projectSetProjectTypes, projectSetProp } from "./project.slice.ts"
import { type ProjectMilestoneFilters } from "./project.types.ts"

export const createProject = createAsyncThunk<Project, ProjectCreateData>(
  `${projectSliceName}/createProject`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createProject(data)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.createProject"
      })
    }

    showResponseMessage(response, "Project created successfully.")

    return response.data?.details?.data as Project
  }
)

export const fetchProject = createAsyncThunk<Project, string>(
  `${projectSliceName}/fetchProjectDetails`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getProject(id)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        // The page tells a deleted project (404) from a failed read by this.
        rejectValue: { status: response.status ?? null },
        flow: "project.fetchProject"
      })
    }

    return response.data as Project
  }
)

export const updateProject = createAsyncThunk<Project, ProjectUpdateData>(
  `${projectSliceName}/updateProject`,
  async ({ id, ...data }, { dispatch, rejectWithValue }) => {
    const response = await services.patchProject(id, data)

    if (!response.ok) {
      return (
        rejectPendingCommitments(response.data, rejectWithValue) ??
        rejectWithHandledError(response, {
          dispatch,
          rejectWithValue,
          flow: "project.updateProject"
        })
      )
    }

    showResponseMessage(response, "Project updated successfully.")

    return response.data?.details?.data as Project
  }
)

export const updateListProject = createAsyncThunk<
  ProjectEditableRowData,
  ProjectEditableRowData
>(
  `${projectSliceName}/updateListProject`,
  async ({ id, ...data }, { dispatch, rejectWithValue }) => {
    const response = await services.patchProject(id, data)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.updateListProject"
      })
    }

    const updatedProject = response.data?.details?.data as Project | undefined

    showResponseMessage(response, "Project updated successfully.")

    return {
      id,
      ...data,
      project_managers: updatedProject?.project_managers
    } as ProjectEditableRowData
  }
)

type UpdateProjectStatusMode = "list" | "grid"

export const updateProjectStatus = createAsyncThunk<
  { id: string; newStatus: string; mode: UpdateProjectStatusMode },
  {
    project: Partial<Project> & { id: string }
    mode: UpdateProjectStatusMode
  }
>(
  `${projectSliceName}/updateProjectStatus`,
  async ({ project, mode }, { dispatch, rejectWithValue }) => {
    const { id, ...data } = project

    const response = await services.patchProject(id, data)

    if (!response.ok) {
      return (
        rejectPendingCommitments(response.data, rejectWithValue) ??
        rejectWithHandledError(response, {
          dispatch,
          rejectWithValue,
          flow: "project.updateProjectStatus"
        })
      )
    }

    showResponseMessage({}, "Project status updated successfully.")

    return {
      id,
      mode,
      newStatus: response.data?.details?.data?.project_status_name as string
    }
  }
)

export const createAndAssignSubcontractor = createAsyncThunk<
  SubcontractorListItem,
  {
    projectId: string
    data: Omit<SubcontractorFormData, "send_invitation"> & { invite?: boolean }
  }
>(
  `${projectSliceName}/createNewProjectSubcontractor`,
  async ({ projectId, data }, { dispatch, rejectWithValue }) => {
    const response = await services.createAndAssignSubcontractor(
      projectId,
      data
    )

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.createAndAssignSubcontractor"
      })
    }

    showResponseMessage(response, "Subcontractor created successfully.")

    return response.data?.details?.data as SubcontractorListItem
  }
)

export const addProjectTypeToProject = createAsyncThunk<
  Project["project_type_details"],
  { projectId: string; data: ProjectTypeFormData }
>(
  `${projectSliceName}/addProjectTypeToProject`,
  async ({ projectId, data }, { dispatch, getState, rejectWithValue }) => {
    const response = await services.addProjectTypeToProject(projectId, data)

    if (!response.ok) {
      // Not a failure: a toast here would cover the confirm dialog it opens.
      if (isRepeatedTypeRefusal(response.data)) {
        return rejectWithValue(response.data ?? null)
      }

      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.addProjectTypeToProject"
      })
    }

    const state = getState() as RootState
    const selectedProjectType = state?.project?.milestonesFilters?.project_type
    const updatedTypes = response.data?.details?.data
      ?.project_type_details as ProjectTypeDetail[]

    if (!selectedProjectType) {
      dispatch(
        projectSetProp({
          key: "milestonesFilters",
          value: {
            project_type: updatedTypes?.[0]?.id,
            project_type_assignment: updatedTypes?.[0]?.assignment_id
          } as ProjectMilestoneFilters
        })
      )
    }

    showResponseMessage(response, "Project type added successfully.")

    return response.data?.details?.data
      ?.project_type_details as Project["project_type_details"]
  }
)

export const updateProjectTypeToProject = createAsyncThunk<
  Project["project_type_details"],
  { projectId: string; data: ProjectTypeData }
>(
  `${projectSliceName}/updateProjectTypeToProject`,
  async ({ projectId, data }, { dispatch, rejectWithValue }) => {
    const response = await services.updateProjectTypeToProject(projectId, data)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.updateProjectTypeToProject"
      })
    }

    showResponseMessage(response, "Project type added successfully.")

    return response.data?.details?.data
      ?.project_type_details as Project["project_type_details"]
  }
)

export const recordProjectTypeContractDecision = createAsyncThunk<
  Project["project_type_details"],
  { projectId: string; data: ProjectTypeContractDecisionData }
>(
  `${projectSliceName}/recordProjectTypeContractDecision`,
  async ({ projectId, data }, { dispatch, rejectWithValue }) => {
    const response = await services.recordProjectTypeContractDecision(
      projectId,
      data
    )

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.recordProjectTypeContractDecision"
      })
    }

    const updatedProject = response.data?.details?.data as Project
    dispatch(projectSetProp({ key: "project", value: updatedProject }))

    return updatedProject.project_type_details
  }
)

export const reorderProjectTypes = createAsyncThunk<
  Project["project_type_details"],
  { projectId: string; items: ProjectTypeOrderItem[] },
  { state: RootState }
>(
  `${projectSliceName}/reorderProjectTypes`,
  async ({ projectId, items }, { dispatch, getState, rejectWithValue }) => {
    const previous = getState()?.project?.project?.project_type_details || []

    // Move the tabs before the request so they stay where they were dropped;
    // the response is authoritative and replaces this once it arrives.
    // Keyed on the assignment: one type carried twice collapses two tabs into
    // one entry.
    const optimistic = orderByIds(
      previous,
      items.map((item) => item.assignment_id ?? item.project_type_id),
      instanceKeyOf
    )

    if (optimistic) dispatch(projectSetProjectTypes(optimistic))

    const response = await services.reorderProjectTypes(projectId, { items })

    if (!response.ok) {
      dispatch(projectSetProjectTypes(previous))
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.reorderProjectTypes"
      })
    }

    return response.data?.details?.data
      ?.project_type_details as Project["project_type_details"]
  }
)

export const fetchProjectLogHistory = createAsyncThunk<AuditLogEntry[], string>(
  `${projectSliceName}/fetchProjectLogHistory`,
  async (projectId, { dispatch, rejectWithValue }) => {
    const response = await services.getProjectLogHistory(projectId)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.fetchProjectLogHistory"
      })
    }

    const data = response.data as AuditLogEntry[]

    return Array.isArray(data) ? (data as AuditLogEntry[]) : []
  }
)

export const removeProjectTypeToProject = createAsyncThunk<
  Project["project_type_details"],
  {
    projectId: string
    project_type_id: string
    assignment_id?: string | null
    reverse_contract?: boolean
  }
>(
  `${projectSliceName}/removeProjectTypeToProject`,
  async (
    { projectId, project_type_id, assignment_id, reverse_contract },
    { dispatch, getState, rejectWithValue }
  ) => {
    const response = await services.removeProjectTypeToProject(projectId, {
      project_type_id,
      assignment_id,
      reverse_contract
    })

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.removeProjectTypeToProject"
      })
    }

    const state = getState() as RootState
    const filters = state?.project?.milestonesFilters
    const updatedProject = response.data?.details?.data as Project
    const updatedTypes =
      updatedProject.project_type_details as ProjectTypeDetail[]

    dispatch(projectSetProp({ key: "project", value: updatedProject }))

    // On the assignment: a type-id match would close another instance's tab.
    const removedTheOpenTab = assignment_id
      ? filters?.project_type_assignment === assignment_id
      : filters?.project_type === project_type_id

    if (removedTheOpenTab) {
      dispatch(
        projectSetProp({
          key: "milestonesFilters",
          value: {
            project_type: updatedTypes?.[0]?.id || null,
            project_type_assignment: updatedTypes?.[0]?.assignment_id || null
          } as ProjectMilestoneFilters
        })
      )
    }

    showResponseMessage(response, "Project type removed successfully.")

    return updatedTypes
  }
)

export const saveProjectTypeAsTemplate = createAsyncThunk<
  { id: string; name: string },
  {
    projectId: string
    project_type_id: string
    assignment_id?: string | null
    name: string
  }
>(
  `${projectSliceName}/saveProjectTypeAsTemplate`,
  async (
    { projectId, project_type_id, assignment_id, name },
    { dispatch, rejectWithValue }
  ) => {
    const response = await services.saveProjectTypeAsTemplate(projectId, {
      project_type_id,
      assignment_id,
      name
    })

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.saveProjectTypeAsTemplate"
      })
    }

    showResponseMessage(response, "Saved as a new project type template.")

    return response.data?.details?.data as { id: string; name: string }
  }
)
